import React, { useState } from 'react';

const AccountSettings = ({ user, onSave }) => {
  const [formData, setFormData] = useState({
    email: user?.attributes?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    deleteAccount: false
  });
  
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      onSave(false, 'Email cannot be empty.');
      return;
    }
    
    try {
      // In a real app, you would update the user's email
      console.log('Updating email to:', formData.email);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onSave(true, 'Email updated successfully! Please check your inbox to verify your new email.');
    } catch (error) {
      console.error('Error updating email:', error);
      onSave(false, 'Failed to update email. Please try again.');
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (!formData.currentPassword) {
      onSave(false, 'Current password is required.');
      return;
    }
    
    if (formData.newPassword.length < 8) {
      onSave(false, 'New password must be at least 8 characters long.');
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      onSave(false, 'New passwords do not match.');
      return;
    }
    
    try {
      // In a real app, you would update the user's password
      console.log('Updating password');
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Clear password fields
      setFormData(prevData => ({
        ...prevData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
      onSave(true, 'Password updated successfully!');
    } catch (error) {
      console.error('Error updating password:', error);
      onSave(false, 'Failed to update password. Please check your current password and try again.');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      // In a real app, you would delete the user's account
      console.log('Deleting account');
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Redirect to home page or show a goodbye message
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      onSave(false, 'Failed to delete account. Please try again.');
    }
  };

  return (
    <div className="profile-section">
      <h2>Account Settings</h2>
      <p className="section-description">
        Manage your account settings and security preferences.
      </p>
      
      <div className="account-section">
        <h3>Email Address</h3>
        <form onSubmit={handleEmailUpdate} className="profile-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email address"
            />
          </div>
          
          <div className="form-actions">
            <button type="submit" className="btn-primary">Update Email</button>
          </div>
        </form>
      </div>
      
      <div className="account-section">
        <h3>Change Password</h3>
        <form onSubmit={handlePasswordUpdate} className="profile-form">
          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              placeholder="Enter your current password"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Enter your new password"
            />
            <small>Password must be at least 8 characters long</small>
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your new password"
            />
          </div>
          
          <div className="form-actions">
            <button type="submit" className="btn-primary">Update Password</button>
          </div>
        </form>
      </div>
      
      <div className="account-section danger-zone">
        <h3>Delete Account</h3>
        <p className="warning-text">
          Warning: This action cannot be undone. All your data will be permanently deleted.
        </p>
        
        {!showDeleteConfirm ? (
          <button 
            className="btn-danger"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete My Account
          </button>
        ) : (
          <div className="delete-confirmation">
            <p>Are you sure you want to delete your account? This action cannot be undone.</p>
            <div className="confirmation-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={handleDeleteAccount}
              >
                Yes, Delete My Account
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div className="account-section">
        <h3>Data Export</h3>
        <p>
          You can download all your data in a machine-readable format.
        </p>
        <button className="btn-secondary">
          Export My Data
        </button>
      </div>
      
      <div className="account-section">
        <h3>Connected Accounts</h3>
        <p>
          Connect your RangeDat account with other services.
        </p>
        <div className="connected-accounts">
          <div className="connected-account">
            <div className="account-info">
              <span className="account-name">Google</span>
              <span className="account-status">Not Connected</span>
            </div>
            <button className="btn-secondary">Connect</button>
          </div>
          <div className="connected-account">
            <div className="account-info">
              <span className="account-name">Facebook</span>
              <span className="account-status">Not Connected</span>
            </div>
            <button className="btn-secondary">Connect</button>
          </div>
          <div className="connected-account">
            <div className="account-info">
              <span className="account-name">Apple</span>
              <span className="account-status">Not Connected</span>
            </div>
            <button className="btn-secondary">Connect</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
